import axios, { type AxiosInstance } from 'axios'
import type { Router } from 'vue-router'
import type { useAuthStore } from '@/stores/auth'

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
})

let installed = false

/**
 * Подключает interceptors к axios после того как Pinia и Router инициализированы.
 * Вызывается один раз из main.ts.
 */
export function setupApiClient(router: Router, authStore: ReturnType<typeof useAuthStore>): void {
  if (installed) return
  installed = true

  api.interceptors.request.use((config) => {
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    return config
  })

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // F-I5: несколько одновременных query'ев после token expiry дают залп 401.
        // Один из них должен сделать clear() + redirect, остальные — no-op.
        // Проверяем token === null на входе: первый 401 ставит null, последующие
        // увидят null и пропустят redirect. Иначе ?redirect= перезапишется
        // последним запросом, который часто не равен текущему URL пользователя.
        if (authStore.token === null) {
          return Promise.reject(error)
        }
        authStore.clear()
        const currentPath = router.currentRoute.value.fullPath
        if (router.currentRoute.value.name !== 'login') {
          router.push({ name: 'login', query: { redirect: currentPath } })
        }
      }
      return Promise.reject(error)
    },
  )
}

/**
 * Скачивает crop-картинку через axios (с Authorization header) и возвращает
 * blob: URL. **Caller MUST call URL.revokeObjectURL(url) when the URL is no
 * longer needed** (typically via onScopeDispose / onBeforeUnmount), иначе
 * underlying Blob удерживается на всё время жизни документа.
 */
export async function fetchCropAsBlobUrl(detectionId: number): Promise<string> {
  const response = await api.get<Blob>(`/detections/${detectionId}/crop`, {
    responseType: 'blob',
  })
  return URL.createObjectURL(response.data)
}

/**
 * Скачивает фото машины (vehicle-кроп) index-го по свежести для трека детекции.
 * Caller MUST call URL.revokeObjectURL when done.
 */
export async function fetchVehicleCropAsBlobUrl(detectionId: number, index = 0): Promise<string> {
  const response = await api.get<Blob>(`/detections/${detectionId}/vehicle-crop`, {
    params: { index },
    responseType: 'blob',
  })
  return URL.createObjectURL(response.data)
}

/**
 * Скачивает фото машины (vehicle-кроп) для трека по track_id.
 * Использует эндпоинт GET /tracks/{trackId}/vehicle-crop (фича B).
 * Caller MUST call URL.revokeObjectURL when done.
 */
export async function fetchTrackVehicleCropAsBlobUrl(trackId: string, index = 0): Promise<string> {
  const response = await api.get<Blob>(`/tracks/${trackId}/vehicle-crop`, {
    params: { index },
    responseType: 'blob',
  })
  return URL.createObjectURL(response.data)
}
